import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';
import LoadingOverlay from '../../../components/shared/LoadingOverlay';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import { toast } from '../../../components/shared/toast';
import './RevenueSongDetails.css';

function RevenueSongDetails({ songId, initialSong, onBack }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Edit modal state
  const [editingRecord, setEditingRecord] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

  // Delete modal state
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isSubmittingDelete, setIsSubmittingDelete] = useState(false);

  const fetchSongDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await apiClient(`/revenue/song/${songId}`);
      if (res) {
        setData(res);
      }
    } catch (err) {
      console.error('Error fetching song revenue details:', err);
      toast.error('Failed to load song revenue details');
    } finally {
      setIsLoading(false);
    }
  }, [songId]);

  useEffect(() => {
    if (songId) {
      fetchSongDetails();
    }
  }, [songId, fetchSongDetails]);

  const song = data?.song || initialSong || {};
  const metrics = data?.metrics || {};
  const history = data?.history || [];

  const fmt = (val) => `$${(parseFloat(val) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  // Handle Edit submit
  const handleOpenEdit = (item) => {
    setEditingRecord(item);
    setEditAmount(String(item.amount || 0));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingRecord) return;
    const parsed = parseFloat(editAmount);
    if (isNaN(parsed) || parsed < 0) {
      toast.error('Please enter a valid positive revenue amount');
      return;
    }

    try {
      setIsSubmittingEdit(true);
      const res = await apiClient(`/revenue/${editingRecord.id}`, {
        method: 'PUT',
        body: JSON.stringify({ amount: parsed })
      });

      if (res && res.success) {
        toast.success('Revenue record updated successfully');
        setEditingRecord(null);
        fetchSongDetails();
      } else {
        toast.error(res?.message || 'Failed to update revenue record');
      }
    } catch (err) {
      console.error('Error updating revenue entry:', err);
      toast.error('Failed to update revenue record');
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  // Handle Delete confirm
  const handleConfirmDelete = async () => {
    if (!recordToDelete) return;
    try {
      setIsSubmittingDelete(true);
      const res = await apiClient(`/revenue/${recordToDelete.id}`, {
        method: 'DELETE'
      });

      if (res && res.success) {
        toast.success('Revenue record deleted successfully');
        setRecordToDelete(null);
        fetchSongDetails();
      } else {
        toast.error(res?.message || 'Failed to delete revenue record');
      }
    } catch (err) {
      console.error('Error deleting revenue entry:', err);
      toast.error('Failed to delete revenue record');
    } finally {
      setIsSubmittingDelete(false);
    }
  };

  return (
    <div className="revenue-song-details-container">
      <LoadingOverlay isLoading={isLoading} message="Loading Song Revenue Details..." />

      {/* Header & Breadcrumb */}
      <div className="rsd-header">
        <button type="button" className="rsd-back-btn" onClick={onBack}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back to Revenue
        </button>

        <div className="rsd-breadcrumb">
          Revenue &gt; Songs &gt; <span className="active">{song.name || 'Song Details'}</span>
        </div>

        <div className="rsd-title-group">
          <h1 className="rsd-title-sinhala">{song.nameSinhala || song.name}</h1>
          <span className="rsd-title-english">{song.name}</span>
        </div>

        <div className="rsd-last-update">
          Last Time Update Date: <span className="date-val">{metrics.lastUpdateDate || 'N/A'}</span>
        </div>
      </div>

      {/* Main Details Panel (2-column layout matching mockup) */}
      <div className="rsd-main-panel">
        {/* Left Column: Lifetime & Remaining Cards */}
        <div className="rsd-left-box">
          <div className="rsd-left-header">
            <h3 className="rsd-section-title">Lifetime <span className="muted">Income</span></h3>
            <div className="rsd-date-range-pill">
              {metrics.minDate !== 'N/A' && metrics.maxDate !== 'N/A'
                ? `${metrics.minDate} to ${metrics.maxDate}`
                : 'All Time Range'}
            </div>
          </div>

          <div className="rsd-income-card blue-card">
            <div className="rsd-card-amount">{fmt(metrics.grossIncome)}</div>
            <div className="rsd-card-label">Income</div>
          </div>

          <div className="rsd-outgoing-subtext">
            Distribution <span className="highlight-pct">30%</span> outgoing — {fmt(metrics.distributorAmount || 0)}
          </div>

          <div className="rsd-income-card blue-card">
            <div className="rsd-card-amount">{fmt(metrics.afterDistributor || 0)}</div>
            <div className="rsd-card-label">Remaining</div>
          </div>
        </div>

        {/* Right Column: Ownership & Total Breakdown */}
        <div className="rsd-right-box">
          {/* Top Section: Ownership Breakdown */}
          <div className="rsd-ownership-section">
            <div className="rsd-ownership-top-row">
              <div className="rsd-ownership-info">
                <h3 className="rsd-section-title">Ownership of this song</h3>
                <p className="rsd-ownership-desc">
                  Record Label, Lyrics &amp; Music Rights held by us <strong>{metrics.ownershipPct}%</strong>
                </p>
              </div>
              <div className="rsd-ownership-total-amt">{fmt(metrics.afterDistributor || 0)}</div>
            </div>

            {/* 3 Share Cards */}
            <div className="rsd-shares-row">
              {/* Label Share */}
              <div className={`rsd-share-card ${song.isRecordLabel ? 'card-green' : 'card-red'}`}>
                <div className="rsd-share-pill">{metrics.labelPct}%</div>
                <div className="rsd-share-amt">{fmt(metrics.labelShare)}</div>
                <div className="rsd-share-label">Label - {song.labelNames || 'Ransilu'}</div>
              </div>

              {/* Lyrics Share */}
              <div className={`rsd-share-card ${song.isLyrics ? 'card-green' : 'card-red'}`}>
                <div className="rsd-share-pill">{metrics.lyricsPct}%</div>
                <div className="rsd-share-amt">{fmt(metrics.lyricsShare)}</div>
                <div className="rsd-share-label">Lyrics - {song.lyrics || 'Name'}</div>
              </div>

              {/* Music Share */}
              <div className={`rsd-share-card ${song.isMusician ? 'card-green' : 'card-red'}`}>
                <div className="rsd-share-pill">{metrics.musicPct}%</div>
                <div className="rsd-share-amt">{fmt(metrics.musicShare)}</div>
                <div className="rsd-share-label">Music - {song.music || 'Name'}</div>
              </div>
            </div>
          </div>

          {/* Bottom Section: Total Earning & Outgoing */}
          <div className="rsd-summary-section">
            <h3 className="rsd-section-title">Total Earning &amp; outgoing of this song</h3>

            <div className="rsd-breakdown-row">
              {/* Earning Card */}
              <div className="rsd-breakdown-card card-green-large">
                <div className="rsd-share-pill pill-green">{metrics.ownershipPct}%</div>
                <div className="rsd-breakdown-amt green-text">{fmt(metrics.totalEarning)}</div>
                <div className="rsd-breakdown-label">Earning</div>
              </div>

              {/* Outgoing Card */}
              <div className="rsd-breakdown-card card-pink-large">
                <div className="rsd-share-pill pill-pink">{metrics.outgoingPct}%</div>
                <div className="rsd-breakdown-amt pink-text">{fmt(metrics.totalOutgoing)}</div>
                <div className="rsd-breakdown-label">Outgoing</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Timeline: Update History with Edit & Delete Action Buttons */}
      <div className="rsd-history-section">
        <h3 className="rsd-history-title">Update History</h3>

        {history.length === 0 ? (
          <div className="rsd-history-empty">No update history recorded yet.</div>
        ) : (
          <div className="rsd-timeline">
            {history.map((item) => (
              <div key={item.id} className="rsd-timeline-item">
                <div className="rsd-timeline-node">
                  <div className="rsd-node-dot"></div>
                  <div className="rsd-node-line"></div>
                </div>
                <div className="rsd-timeline-content">
                  <span className="rsd-timeline-date">{item.date}</span>
                  <span className="rsd-timeline-action">Add {fmt(item.amount)}</span>
                </div>
                <div className="rsd-item-actions">
                  <button
                    type="button"
                    className="rsd-action-btn edit-btn"
                    title="Edit Amount"
                    onClick={() => handleOpenEdit(item)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="rsd-action-btn delete-btn"
                    title="Delete Entry"
                    onClick={() => setRecordToDelete(item)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Revenue Entry Modal */}
      {editingRecord && (
        <div className="rsd-modal-overlay">
          <div className="rsd-modal-card">
            <div className="rsd-modal-header">
              <h3>Edit Revenue Entry</h3>
              <button
                type="button"
                className="rsd-modal-close"
                onClick={() => setEditingRecord(null)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSaveEdit}>
              <div className="rsd-modal-body">
                <label className="rsd-input-label">Revenue Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="rsd-input-field"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  placeholder="Enter new revenue amount"
                  required
                  autoFocus
                />
              </div>
              <div className="rsd-modal-footer">
                <button
                  type="button"
                  className="rsd-btn-secondary"
                  onClick={() => setEditingRecord(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rsd-btn-primary"
                  disabled={isSubmittingEdit}
                >
                  {isSubmittingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Primary Delete Confirmation Modal */}
      {recordToDelete && (
        <ConfirmationModal
          isOpen={!!recordToDelete}
          type="danger"
          title="Delete Revenue Entry"
          message={`Are you sure you want to delete this revenue entry of $${recordToDelete.amount}? This will recalculate the song's total income, earning, and outgoing amounts.`}
          confirmLabel="Yes, Delete"
          cancelLabel="Cancel"
          confirmText="Yes, Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={() => setRecordToDelete(null)}
          isSubmitting={isSubmittingDelete}
        />
      )}
    </div>
  );
}

export default RevenueSongDetails;
